'use client';

import { useState, useEffect, useMemo } from 'react';
import { Search, ChevronRight, ChevronDown, Loader2, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { EndpointListItem } from './endpoint-list-item';
import { getProviderEndpoints } from '@/lib/integrations/api';
import type { ProviderEndpoint, ProviderEndpointGroup, HttpMethod } from '@/lib/integrations/types';

interface EndpointSidebarProps {
  provider: string;
  selectedEndpoint?: ProviderEndpoint | null;
  onSelectEndpoint: (endpoint: ProviderEndpoint) => void;
}

const METHOD_ORDER: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export function EndpointSidebar({
  provider,
  selectedEndpoint,
  onSelectEndpoint,
}: EndpointSidebarProps) {
  const [endpoints, setEndpoints] = useState<ProviderEndpointGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<HttpMethod>>(new Set(['GET', 'POST']));

  useEffect(() => {
    async function fetchEndpoints() {
      setLoading(true);
      setError(null);
      try {
        const data = await getProviderEndpoints(provider);
        setEndpoints(data);
      } catch (err) {
        setError('Failed to load endpoints');
      } finally {
        setLoading(false);
      }
    }
    fetchEndpoints();
  }, [provider]);

  // Group endpoints by method
  const groupedEndpoints = useMemo(() => {
    if (!endpoints?.endpoints) return new Map<HttpMethod, ProviderEndpoint[]>();

    const filtered = endpoints.endpoints.filter(ep => {
      if (!search) return true;
      const searchLower = search.toLowerCase();
      return (
        ep.path.toLowerCase().includes(searchLower) ||
        ep.description?.toLowerCase().includes(searchLower)
      );
    });

    const groups = new Map<HttpMethod, ProviderEndpoint[]>();
    for (const method of METHOD_ORDER) {
      const methodEndpoints = filtered.filter(ep => ep.method === method);
      if (methodEndpoints.length > 0) {
        groups.set(method, methodEndpoints);
      }
    }
    return groups;
  }, [endpoints, search]);

  const toggleGroup = (method: HttpMethod) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(method)) {
        next.delete(method);
      } else {
        next.add(method);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-center px-4">
        <AlertCircle className="h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!endpoints || endpoints.endpoints.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-center px-4">
        <p className="text-sm text-muted-foreground">
          No endpoint metadata available for this provider.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          You can still enter endpoints manually.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search endpoints..."
            className="pl-8 h-9"
          />
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2">
          {Array.from(groupedEndpoints.entries()).map(([method, methodEndpoints]) => (
            <div key={method} className="mb-2">
              <button
                onClick={() => toggleGroup(method)}
                className="flex items-center gap-1 w-full px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
              >
                {expandedGroups.has(method) ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                {method}
                <span className="ml-auto text-muted-foreground/60">
                  {methodEndpoints.length}
                </span>
              </button>

              {expandedGroups.has(method) && (
                <div className="space-y-0.5 mt-1">
                  {methodEndpoints.map((ep, idx) => (
                    <EndpointListItem
                      key={`${ep.method}-${ep.path}-${idx}`}
                      endpoint={ep}
                      selected={
                        selectedEndpoint?.method === ep.method &&
                        selectedEndpoint?.path === ep.path
                      }
                      onClick={() => onSelectEndpoint(ep)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="p-3 border-t text-xs text-muted-foreground">
        {endpoints.endpoints.length} endpoints available
      </div>
    </div>
  );
}

export default EndpointSidebar;
